import { useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  X,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  CalendarDays,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FILE_SLOTS = [
  {
    key: 'programs_file',
    label: 'Programs',
    description: 'Transaction-level program data (MLS GO, NFL FLAG, etc.)',
    sub: 'Maps to Small Business segment',
  },
  {
    key: 'sb_manually_written_file',
    label: 'SB Manually Written',
    description: 'Account-level Small Business policy export',
    sub: 'Maps to Small Business segment',
  },
  {
    key: 'yam_fit_nil_file',
    label: 'YAM / Fitness / NIL',
    description: 'Profit-center summary by vertical',
    sub: 'Maps to Youth & Amateur Sports / Fitness / College/NIL segments',
  },
];

const SEGMENT_ORDER = [
  'Youth & Amateur Sports',
  'Fitness',
  'College/NIL',
  'Small Business',
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// Helper: get current week (Monday-Sunday) by default
const getDefaultWeek = () => {
  const today = new Date();
  const day = today.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { week_start: fmt(monday), week_end: fmt(sunday) };
};

const FileSlot = ({ slot, file, onSelect, onClear }) => {
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    onSelect(slot.key, f);
  };
  return (
    <Card
      data-testid={`brokerage-upload-slot-${slot.key}`}
      className={`border-2 transition-all ${file ? 'border-blue-500 bg-blue-50/30' : 'border-dashed border-slate-300 hover:border-slate-400'}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-start gap-3 flex-1">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${file ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}
            >
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 text-sm">{slot.label}</p>
              <p className="text-xs text-slate-600 leading-snug">{slot.description}</p>
              <p className="text-[11px] text-slate-500 italic mt-1">{slot.sub}</p>
            </div>
          </div>
          {file && (
            <button
              onClick={() => onClear(slot.key)}
              data-testid={`brokerage-upload-clear-${slot.key}`}
              className="p-1 rounded hover:bg-slate-200 text-slate-500"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {file ? (
          <div className="flex items-center gap-2 text-sm bg-white rounded border border-slate-200 px-3 py-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="text-slate-700 truncate flex-1">{file.name}</span>
            <span className="text-xs text-slate-500 flex-shrink-0">{(file.size / 1024).toFixed(1)} KB</span>
          </div>
        ) : (
          <label
            htmlFor={`file-${slot.key}`}
            className="flex items-center justify-center gap-2 text-sm text-blue-600 hover:text-blue-700 cursor-pointer bg-white border border-slate-200 rounded px-3 py-2.5 hover:border-blue-300 transition-all"
          >
            <Upload className="w-4 h-4" />
            <span>Click to choose Excel file</span>
            <input
              id={`file-${slot.key}`}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleChange}
              data-testid={`brokerage-upload-input-${slot.key}`}
            />
          </label>
        )}
      </CardContent>
    </Card>
  );
};

const PreviewTable = ({ preview }) => {
  const segs = preview?.combined_segments || {};
  const totals = preview?.totals || {};
  return (
    <Card data-testid="brokerage-upload-preview" className="border-blue-200 shadow-sm">
      <CardHeader className="bg-blue-50/40 border-b">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Preview — Aggregated Numbers
            </CardTitle>
            <CardDescription>
              These are the values that will populate the Brokerage dashboard for{' '}
              <span className="font-semibold text-slate-900">
                {preview.week_start} → {preview.week_end}
              </span>
              .
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Segment</TableHead>
              <TableHead className="text-right font-semibold">New Business Premium</TableHead>
              <TableHead className="text-right font-semibold">Renewal Premium</TableHead>
              <TableHead className="text-right font-semibold">Written Premium (NB + Renewal)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SEGMENT_ORDER.map((seg) => {
              const v = segs[seg] || { new_business: 0, renewal: 0 };
              const total = (v.new_business || 0) + (v.renewal || 0);
              return (
                <TableRow key={seg} data-testid={`preview-row-${seg.replace(/[^a-zA-Z]/g, '')}`}>
                  <TableCell className="font-medium">{seg}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-900">
                    {formatCurrency(v.new_business)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-slate-900">
                    {formatCurrency(v.renewal)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-semibold text-slate-900">
                    {formatCurrency(total)}
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow className="bg-blue-50/60 border-t-2 border-blue-200 font-bold">
              <TableCell className="font-bold">Total</TableCell>
              <TableCell className="text-right tabular-nums text-blue-900">
                {formatCurrency(totals.new_business)}
              </TableCell>
              <TableCell className="text-right tabular-nums text-blue-900">
                {formatCurrency(totals.renewal)}
              </TableCell>
              <TableCell className="text-right tabular-nums text-blue-900">
                {formatCurrency(totals.written_premium)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Per-file breakdown */}
        <div className="p-5 border-t bg-slate-50/50">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-3">
            Per-file contribution
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {Object.entries(preview.breakdown_per_file || {}).map(([fileKey, fileSegs]) => {
              const fileMeta = preview.files_metadata?.[fileKey];
              if (!fileMeta) return null;
              const fileNB = Object.values(fileSegs || {}).reduce(
                (s, x) => s + (x?.new_business || 0),
                0
              );
              const fileRen = Object.values(fileSegs || {}).reduce(
                (s, x) => s + (x?.renewal || 0),
                0
              );
              return (
                <div
                  key={fileKey}
                  className="bg-white rounded-md border border-slate-200 p-3"
                  data-testid={`preview-file-${fileKey}`}
                >
                  <p className="text-xs font-semibold text-slate-700 truncate">
                    {fileMeta.filename}
                  </p>
                  <p className="text-[11px] text-slate-500 mb-2">
                    {fileMeta.stats?.rows_classified ?? 0} of {fileMeta.stats?.rows_total ?? 0} rows
                    used
                  </p>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">NB:</span>
                    <span className="tabular-nums">{formatCurrency(fileNB)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Renewal:</span>
                    <span className="tabular-nums">{formatCurrency(fileRen)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {preview.parse_errors && preview.parse_errors.length > 0 && (
          <div className="p-4 border-t bg-amber-50 border-amber-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Some files had issues:</p>
                <ul className="text-xs text-amber-800 list-disc ml-4 mt-1 space-y-0.5">
                  {preview.parse_errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const BrokerageWeeklyUpload = () => {
  const { isAdmin, isELT } = useAuth();
  const canEdit = isAdmin() || isELT();
  const defaultWeek = getDefaultWeek();
  const [weekStart, setWeekStart] = useState(defaultWeek.week_start);
  const [weekEnd, setWeekEnd] = useState(defaultWeek.week_end);
  const [files, setFiles] = useState({});
  const [preview, setPreview] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const handleFileSelect = useCallback((key, file) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
    setPreview(null);
  }, []);

  const handleFileClear = useCallback((key) => {
    setFiles((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPreview(null);
  }, []);

  const handlePreview = async () => {
    if (!weekStart || !weekEnd) {
      toast.error('Please select the week start and end dates.');
      return;
    }
    if (Object.keys(files).length === 0) {
      toast.error('Please upload at least one Excel file.');
      return;
    }
    setParsing(true);
    setPreview(null);
    try {
      const formData = new FormData();
      formData.append('week_start', weekStart);
      formData.append('week_end', weekEnd);
      for (const [key, file] of Object.entries(files)) {
        formData.append(key, file);
      }
      const res = await axios.post(`${API}/brokerage-weekly/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      toast.success('Preview ready — review numbers below before committing.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to parse files';
      toast.error(typeof msg === 'string' ? msg : 'Failed to parse files');
    } finally {
      setParsing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const res = await axios.post(`${API}/brokerage-weekly/commit`, {
        week_start: preview.week_start,
        week_end: preview.week_end,
        combined_segments: preview.combined_segments,
      });
      const sentTo = res.data?.email_summary?.sent || 0;
      const recipients = res.data?.email_summary?.recipients?.length || 0;
      const emailMsg =
        sentTo > 0
          ? ` • Summary emailed to ${sentTo} ${sentTo === 1 ? 'person' : 'people'}.`
          : recipients === 0
          ? ''
          : ' • Email could not be sent (check SendGrid config).';
      toast.success(
        `Committed! Wrote ${res.data.kpi_values_written} KPI values for ${res.data.week_start} → ${res.data.week_end}.${emailMsg}`
      );
      // Reset
      setFiles({});
      setPreview(null);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to commit data';
      toast.error(typeof msg === 'string' ? msg : 'Failed to commit data');
    } finally {
      setCommitting(false);
    }
  };

  const handleReset = () => {
    setFiles({});
    setPreview(null);
  };

  if (!canEdit) {
    return (
      <Layout>
        <Header title="Brokerage Weekly Upload" />
        <div className="p-6">
          <Card>
            <CardContent className="p-6 text-center text-slate-600">
              You don't have permission to upload Brokerage data. Please contact your admin.
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Brokerage Weekly Upload" />
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Intro */}
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
            Brokerage Weekly Upload
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Upload your three weekly Excel files and we'll automatically aggregate the New Business
            and Renewal premium numbers and populate the Brokerage dashboard for the selected week.
          </p>
        </div>

        {/* Week selector */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-slate-500" />
              <CardTitle className="text-base">Reporting Week</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="week-start" className="text-xs text-slate-600">
                  Week start (Monday)
                </Label>
                <Input
                  id="week-start"
                  type="date"
                  value={weekStart}
                  onChange={(e) => {
                    setWeekStart(e.target.value);
                    setPreview(null);
                  }}
                  data-testid="brokerage-upload-week-start"
                />
              </div>
              <div>
                <Label htmlFor="week-end" className="text-xs text-slate-600">
                  Week end (Sunday)
                </Label>
                <Input
                  id="week-end"
                  type="date"
                  value={weekEnd}
                  onChange={(e) => {
                    setWeekEnd(e.target.value);
                    setPreview(null);
                  }}
                  data-testid="brokerage-upload-week-end"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* File slots */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {FILE_SLOTS.map((slot) => (
            <FileSlot
              key={slot.key}
              slot={slot}
              file={files[slot.key]}
              onSelect={handleFileSelect}
              onClear={handleFileClear}
            />
          ))}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handlePreview}
            disabled={parsing || Object.keys(files).length === 0}
            data-testid="brokerage-upload-preview-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            {parsing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Parsing...
              </>
            ) : (
              <>
                Preview Aggregation <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          {(Object.keys(files).length > 0 || preview) && (
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={parsing || committing}
              data-testid="brokerage-upload-reset-btn"
            >
              Clear All
            </Button>
          )}
          {Object.keys(files).length > 0 && !preview && (
            <Badge variant="secondary" className="ml-auto">
              {Object.keys(files).length} of 3 files selected
            </Badge>
          )}
        </div>

        {/* Preview */}
        {preview && (
          <>
            <PreviewTable preview={preview} />

            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="p-5 flex items-center justify-between gap-4">
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">
                    Ready to commit?
                  </p>
                  <p className="text-sm text-slate-600 mt-1">
                    Clicking <strong>Commit to PULSE</strong> will write the values above to the
                    Brokerage dashboard for the week of{' '}
                    <strong>
                      {preview.week_start} → {preview.week_end}
                    </strong>
                    . You can re-upload to overwrite if anything is wrong.
                  </p>
                </div>
                <Button
                  onClick={handleCommit}
                  disabled={committing}
                  data-testid="brokerage-upload-commit-btn"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  size="lg"
                >
                  {committing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Committing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" /> Commit to PULSE
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default BrokerageWeeklyUpload;
